/**
 * Orders service — EG-78 Phase 1 aggregate.
 *
 * Naming: snake_case match BE JSON per ADR-013.
 * Wrapper: BE return `{ success, data }` → unwrap qua `services/http.ts`.
 * Group filter: FE gửi `channel_group=livestream` → BE EG-376 resolve `IN [FACEBOOK, TIKTOK]`.
 */

import type { B2BChannelType } from '@tcr-erp/shared';
import api from './api';
import { unwrap } from './http';
import type { TenantOrder, OrderSummary, TenantOrderTaxBreakdown, TenantOrderServiceLine } from '@/mocks/orders';
import type { OrderListQuery } from './queryKeys';
import { resolveChannelFilterParams } from '@/pages/Orders/parts/constants';
import { toNumber } from '@/utils/format';

/** EG-78 aggregate response shape (snake_case per ADR-013 D4). */
/** TCR-2129 — tổng hợp CK khác chính sách của đơn (BE tính, FE chỉ đọc). */

/**
 * `receipted_amount` (số đã có phiếu thu) chỉ BE mới ghi; đơn cũ / BE chưa deploy thì thiếu.
 * Giữ `undefined` khi thiếu để nơi dùng phân biệt "chưa có số liệu" với "0 đồng" và rơi về
 * `paid_amount`.
 */
function toReceiptedAmount(value: unknown): number | undefined {
  return value === undefined || value === null ? undefined : toNumber(value);
}

export interface OrderDiscountReview {
  has_deviation?: boolean;
  deviation_count?: number;
  total_deviation_amount?: number;
  returned?: boolean;
  return_note?: string | null;
  returned_by_name?: string | null;
  returned_at?: string | null;
}

export interface OrderListResponse {
  items: TenantOrder[];
  total: number;
  summary: OrderSummary;
}

export interface ConfirmOrderVariables {
  id: string;
  confirm_outstanding_debt?: boolean;
}

/** Raw order from BE API — before FE mapping. */
interface RawOrder {
  _id: string;
  order_number: string;
  order_date?: string;
  channel: string;
  platform?: string | null;
  user_type?: 'personal' | 'organization';
  /**
   * TCR-1353 review C1 — id khách hàng gắn với đơn (Mongo `user_id` gốc,
   * `getOrderDetail` trả nguyên document qua `.lean()`, không whitelist).
   * `edit.tsx` cần field này để hydrate `customer_user_id` trên form —
   * thiếu nó thì `LoyaltyBadge` (gate theo đúng field form này) không bao
   * giờ render trên màn sửa đơn.
   */
  user_id?: string;
  /**
   * TCR-1556 — CK tích luỹ B2B đã trừ vào đơn và số đang giữ trên ví rebate.
   * `getOrderDetail` (BE) trả nguyên document nên hai field này luôn có mặt
   * trên đơn B2B; chúng rụng ở FE chỉ vì `mapRawOrderDetail` là whitelist.
   */
  trade_discount_amount?: number;
  rebate_reserved_amount?: number;
  state: string;
  status_group?: string | null;
  /** TCR-1963 — hạn tự huỷ đơn Nháp (state=DRAFT), ISO string hoặc null. */
  draft_expires_at?: string | null;
  delivery_method?: string;
  fulfillment_progress?: string;
  pos_fulfillment?: TenantOrder['pos_fulfillment'];
  return_info?: TenantOrder['return_info'];
  shipping_state?: string;
  payment?: {
    method?: string;
    non_cash_amount?: number;
    cash_amount?: number;
    /** TCR-1332: tiền mặt khách đưa — dùng để hiện "Khách đưa" trên hoá đơn in. */
    cash_received?: number;
    /** TCR-1332: tiền thừa trả khách. */
    change_amount?: number;
  };
  payment_method?: string;
  payment_state?: string;
  payment_status?: string;
  /** TCR-1967: hoá đơn GTGT đã phát hành, `GET /orders` gắn sẵn cho từng dòng. */
  e_invoices?: TenantOrder['e_invoices'];
  gift_summary?: TenantOrder['gift_summary'];
  shipping?: TenantOrder['shipping'];
  /**
   * Dòng dịch vụ cấp đơn — snapshot lúc tạo đơn (`OrderServiceLine`, BE). Bỏ
   * sót field này ở mapper là bug đã xảy ra thật: BE trả đủ nhưng FE làm rơi
   * âm thầm (whitelist), khiến `orderToMoneyResult` luôn rơi vào nhánh "đơn
   * cũ thiếu snapshot" kể cả với đơn mới.
   */
  service_lines?: TenantOrderServiceLine[];
  buyer_invoice_info?: { name: string; phone?: string };
  summary?: {
    original_price?: number;
    original_price_with_vat?: number;
    direct_discount?: number;
    voucher_discount?: number;
    final_price?: number;
    subtotal?: number;
    shipping_cost?: number;
    /**
     * Phần phí vận chuyển do CTKM freeship gánh (đã kẹp). Phí GỐC vẫn nguyên ở
     * `shipping_cost` để kế toán biết cửa hàng bỏ ra bao nhiêu. Đơn cũ không có
     * field này ⇒ 0.
     */
    shipping_discount?: number;
    /** TCR-2054: tổng khách phải trả (total + ship người mua trả − freeship). Đơn cũ thiếu ⇒ tính lại bằng `resolveCollectTotal`. */
    collect_total?: number;
    /**
     * TCR-1352: số TIỀN đã giảm do khách đổi điểm tích luỹ (`OrderSummary.loyalty_discount`, BE).
     * Chỉ để HIỂN THỊ — `final_price`/`total` BE ghi đã trừ sẵn khoản này. Đơn cũ / đơn không
     * dùng điểm ⇒ thiếu field ⇒ 0.
     */
    loyalty_discount?: number;
    /**
     * TCR-1353 — số điểm tích luỹ đã dùng trên đơn (đơn mới có sẵn; đơn cũ
     * thiếu field ⇒ màn sửa đơn hỏi backend qua `useOrderRedeemedPoints`).
     */
    loyalty_redeem_points?: number;
    vat?: number;
    total?: number;
    total_items?: number;
    total_discount?: number;
    /** Bảng tổng hợp thuế theo thuế suất, snapshot lúc tạo đơn (`OrderTaxBreakdown`, BE). */
    tax_breakdowns?: TenantOrderTaxBreakdown[];
    /**
     * "Doanh thu đơn hàng" (`OrderSummary.order_revenue`, BE). CHƯA gồm VAT —
     * bằng tiền hàng sau chiết khấu + phụ thu, trừ thêm phí ship CHỈ khi đơn
     * tự vận chuyển VÀ người bán trả phí (TCR-1084/1086). KHÁC `net_revenue`
     * ở các đơn tự vận chuyển: `net_revenue` không trừ phí ship. Cũng KHÁC
     * field `order_revenue` cấp đơn ở dưới (đó là số BE tính riêng cho danh
     * sách qua aggregate `$set`, công thức khác, chỉ có ở `GET /orders`).
     */
    order_revenue?: unknown;
    /**
     * Doanh thu chưa VAT cho kế toán (`OrderSummary.net_revenue`, BE) — tổng
     * cơ sở tính thuế, không cộng/trừ phí ship. Khác `order_revenue` ở trên
     * tại các đơn tự vận chuyển.
     */
    net_revenue?: unknown;
    /** TCR-1044: KM tự động (name/discount_amount) + voucher code thật đã áp dụng. */
    applied_promotions?: Array<{
      promotion_id?: string;
      name: string;
      type?: string;
      discount_amount: number;
      /**
       * TCR-1244 — `shipping` = khoản giảm PHÍ VẬN CHUYỂN, không phải giảm tiền
       * hàng. Thiếu nó (đơn cũ) thì coi như khoản giảm tiền hàng, đúng hành vi
       * trước đây.
       */
      applied_to?: string;
    }>;
    applied_vouchers?: Array<{
      voucher_id?: string;
      voucher_code?: string;
      code?: string;
      discount_amount: number;
    }>;
    promotion_gifts?: Array<{
      product_name?: string;
      name?: string;
      quantity?: number;
    }>;
  };
  subtotal?: number;
  discount?: number;
  order_revenue?: number | null;
  shipping_fee_estimate?: number | null;
  shipping_fee_actual?: number | null;
  surcharge_amount?: number;
  created_by_name?: string;
  /**
   * TCR-1901: id chi nhánh xử lý đơn. `GET /orders` trả qua `$project` (đã
   * `$toString`), `GET /orders/:id` trả thẳng từ document.
   */
  store_id?: string;
  /** TCR-1668: tên chi nhánh, do `GET /orders` $lookup từ `stores` theo `store_id`. */
  store_name?: string;
  shipping_city_name?: string;
  shipping_ward_name?: string;
  paid_amount?: number;
  /** Số tiền ĐÃ có phiếu thu (PT − PC) — khác `paid_amount` (gồm tiền quầy chưa phiếu). */
  receipted_amount?: number;
  total_paid?: number;
  payment_type?: string;
  payments?: Array<{ method?: string }>;
  items?: Array<Record<string, unknown>>;
  note?: string;
  /** Ghi chú vận chuyển, top-level — KHÔNG lồng trong `shipping` (TCR-1221). */
  shipping_note?: string;
  created_at: string;
  commission_staff?: Array<{ staff_id: string; role_code: string }>;
  process_by?: string;
  cashier_name?: string;
  pre_order_flag?: boolean;
  fulfillment_type?: string;
  accounting?: TenantOrder['accounting'];
  e_invoice?: TenantOrder['e_invoice'];
  /** TCR-1042: snapshot từ Customer.b2b_channel lúc tạo đơn. READ-ONLY trên FE. */
  b2b_channel?: B2BChannelType;
  /** TCR-2129 — cờ/tổng hợp CK khác chính sách (BE tính, FE chỉ đọc). */
  discount_review?: OrderDiscountReview | null;
  /** TCR-2129 — nhật ký lý do CK khác chính sách (chỉ chi tiết đơn). */
  discount_override_logs?: unknown[];
  /**
   * Task 9 (order-edit-pos-parity Phần B) — CHỈ có mặt trên response của
   * `PATCH /orders/:id` khi payload gửi `applied_voucher_codes`. BE tự
   * re-evaluate/validate lại voucher lúc lưu (`orders-aggregate.service.ts`,
   * `voucher_result`); FE không tự tính, chỉ đọc lại để báo người dùng khi
   * `applied === false` (voucher không áp lại được, vd hết lượt dùng).
   */
  voucher_result?: { applied: boolean; reason?: string };
  delivery_date?: string;
  /** TCR-1019 (đợt 2) — ai/khi nào đưa đơn về ĐÚNG trạng thái hiện tại (BE resolve sẵn). */
  state_handler_name?: string;
  state_handler_changed_at?: string;
  /** Lịch sử trạng thái đơn hàng (đặt hàng → xử lý → giao → hoàn thành...). */
  state_history?: Array<{
    state: string;
    changed_at: string;
    changed_by?: string;
    changed_by_name?: string;
    note?: string;
  }>;
  process_logs?: TenantOrder['process_logs'];
  tags?: string[];
  skipped?: boolean;
  /**
   * [Final review fix round 1, 2026-09-07] TCR-1522 — BE (agent song song,
   * `b2b-order-credit-approval.service.ts`) gắn field này lúc tạo đơn B2B tổ
   * chức vượt hạn mức công nợ hiệu lực. Trước fix này, `TenantOrder` (xem
   * `mocks/orders.ts`) đã khai báo field nhưng KHÔNG mapper nào (mapRawOrder/
   * mapRawOrderDetail) đọc từ `RawOrder` — field luôn `undefined` dù BE trả
   * đúng, cùng lớp lỗi whitelist-mapper-nuốt-field đã xảy ra nhiều lần trong
   * file này (xem các comment TCR-1019/1042/1073/1074/1143/1353 phía trên).
   */
  credit_approval_status?: 'not_required' | 'pending' | 'approved' | 'rejected';
  /** TCR-1823: cùng lớp field trên — mapper phải đọc tường minh, không suy ra được từ status. */
  credit_approval_reason?: string | null;
}

function mapSummary(raw: RawOrder['summary']) {
  if (!raw) return undefined;
  return {
    original_price: raw.original_price,
    original_price_with_vat: raw.original_price_with_vat,
    direct_discount: raw.direct_discount,
    voucher_discount: raw.voucher_discount,
    final_price: raw.final_price,
    shipping_cost: raw.shipping_cost,
    // TCR-1244: phần phí ship do CTKM freeship gánh. Thiếu dòng này thì màn
    // chi tiết, popup tóm tắt và màn sửa đơn đều không hiện dòng "Miễn phí
    // vận chuyển" — cùng lớp lỗi whitelist đã nuốt ba field ngay dưới.
    shipping_discount: raw.shipping_discount,
    // TCR-2054: tổng khách phải trả — mẫu số trạng thái thanh toán / số tiền QR. Whitelist: thiếu
    // dòng này thì FE lùi về summary.total (không gồm ship người mua trả).
    collect_total: raw.collect_total,
    // TCR-1352: khoản giảm do đổi điểm tích luỹ — cùng lớp lỗi whitelist ở
    // trên: không liệt kê tường minh thì field biến mất trước khi tới UI, ba
    // màn (chi tiết/popup/sửa đơn) không hiện được dòng "Điểm tích luỹ quy đổi".
    loyalty_discount: raw.loyalty_discount,
    // TCR-1353: cùng lớp lỗi whitelist ghi ở dòng trên — không liệt kê tường
    // minh thì `order.summary.loyalty_redeem_points` biến mất trước khi tới
    // `edit.tsx`, khiến đơn MỚI (có sẵn field) bị coi nhầm là đơn cũ thiếu
    // field, tốn thêm một request `useOrderRedeemedPoints` vô ích.
    loyalty_redeem_points: raw.loyalty_redeem_points,
    vat: raw.vat,
    total: raw.total ?? 0,
    total_items: raw.total_items ?? 0,
    // TCR-1019: 3 field này BỊ WHITELIST NUỐT ÂM THẦM trước Task 19 fix — BE
    // luôn trả đủ (`OrderSummary`, `pos-order.service.ts`), nhưng vì
    // `mapSummary` không liệt kê tường minh, chúng biến mất trước khi tới
    // `orderToMoneyResult`, khiến đơn MỚI vẫn hiện như đơn cũ thiếu snapshot.
    // Test lực (`order.service.test.ts`) đã xác nhận: gỡ 1 trong 3 dòng dưới
    // là test đỏ ngay.
    tax_breakdowns: raw.tax_breakdowns,
    order_revenue: raw.order_revenue,
    net_revenue: raw.net_revenue,
    applied_promotions: raw.applied_promotions,
    applied_vouchers: raw.applied_vouchers,
    promotion_gifts: raw.promotion_gifts,
  };
}

/**
 * Pass-through combo_* cho `mapRawOrder` (items của LIST đến dưới dạng
 * `Record<string, unknown>`): giữ NGUYÊN `null` — `groupOrderItemsByCombo` dựa
 * vào tín hiệu "BE trả null tường minh", ép về `undefined` là mất tín hiệu.
 */
function passThroughString(value: unknown): string | null | undefined {
  if (value === null || value === undefined) return value as null | undefined;
  return String(value);
}

function passThroughNumber(value: unknown): number | null | undefined {
  if (value === null || value === undefined) return value as null | undefined;
  return Number(value);
}

function mapRawOrder(raw: RawOrder): TenantOrder & { platform?: string | null } {
  const rawItems = (raw as unknown as Record<string, unknown>).items as Array<Record<string, unknown>> | undefined;
  return {
    _id: raw._id,
    order_number: raw.order_number,
    channel: raw.channel as TenantOrder['channel'],
    platform: raw.platform,
    user_type: (raw as { user_type?: TenantOrder['user_type'] }).user_type,
    // TCR-1522 — `mapRawOrder()` (màn DANH SÁCH) trước đây KHÔNG map field này,
    // chỉ `mapRawOrderDetail()` (màn chi tiết) có (xem doc-comment ở
    // `RawOrder.user_id` phía trên) — cùng lớp đứt gãy dữ liệu với BE
    // (`orders-aggregate.service.ts` $project cũng từng thiếu `user_id` ở
    // listOrders(), đã fix riêng ở repo BE). Thiếu field này thì badge "Quá
    // công nợ" (`useOrderColumns.tsx`, match theo `row.user_id`) không bao
    // giờ hiện trên trang danh sách dù BE đã trả đúng dữ liệu.
    user_id: raw.user_id ? String(raw.user_id) : undefined,
    state: raw.state as TenantOrder['state'],
    status_group: raw.status_group as TenantOrder['status_group'],
    // TCR-1963 — mapper là whitelist; thiếu dòng này thì badge đếm ngược Nháp không hiện.
    draft_expires_at: (raw as { draft_expires_at?: string | null }).draft_expires_at ?? null,
    delivery_method: raw.delivery_method,
    fulfillment_progress: raw.fulfillment_progress,
    pos_fulfillment: raw.pos_fulfillment,
    return_info: raw.return_info,
    shipping_state: raw.shipping_state,
    total_amount: raw.summary?.total ?? 0,
    subtotal: raw.subtotal ?? raw.summary?.final_price,
    discount: raw.discount
      ?? ((raw.summary?.direct_discount ?? 0) + (raw.summary?.voucher_discount ?? 0)),
    summary: mapSummary(raw.summary),
    // Field cấp đơn, KHÁC `summary.order_revenue` (xem comment ở `RawOrder`)
    // — giữ nguyên như trước Task 19, chỉ BE aggregate `GET /orders` set field
    // này.
    order_revenue: raw.order_revenue ?? undefined,
    service_lines: raw.service_lines,
    shipping_fee_estimate: raw.shipping_fee_estimate ?? undefined,
    shipping_fee_actual: raw.shipping_fee_actual ?? undefined,
    created_by_name: raw.created_by_name,
    // TCR-1901: KHÔNG bỏ `store_id` ở đây. Mapper whitelist field nên thiếu một
    // dòng là màn chi tiết đơn mất hẳn chi nhánh — panel QR im lặng rơi về tài
    // khoản mặc định của tenant thay vì tài khoản của chi nhánh.
    store_id: raw.store_id,
    store_name: raw.store_name,
    shipping_city_name: raw.shipping_city_name,
    shipping_ward_name: raw.shipping_ward_name,
    delivery_date: raw.delivery_date,
    state_handler_name: raw.state_handler_name,
    state_handler_changed_at: raw.state_handler_changed_at,
    state_history: raw.state_history,
    process_logs: raw.process_logs,
    paid_amount: (raw as any).paid_amount ?? (raw as any).total_paid ?? raw.summary?.total ?? 0,
    receipted_amount: toReceiptedAmount((raw as any).receipted_amount),
    customer: ((raw as any).customer && (raw as any).customer.name) ? {
      name: (raw as any).customer.name,
      phone: (raw as any).customer.phone,
    } : {
      name: raw.buyer_invoice_info?.name ?? raw.shipping?.address?.name ?? '',
      phone: raw.buyer_invoice_info?.phone ?? raw.shipping?.address?.phone,
    },
    buyer_invoice_info: raw.buyer_invoice_info || (raw as any).buyer_invoice_info,
    cashier: raw.cashier_name
      ? { name: raw.cashier_name }
      : (raw as any).process_by_staff
        ? { name: ((raw as any).process_by_staff as any).name }
        : (raw as any).care_staff
          ? { name: ((raw as any).care_staff as any).name }
          : (raw as any).created_by_name
            ? { name: (raw as any).created_by_name }
            : undefined,
    payment_method: raw.payment?.method || raw.payment_method || raw.payments?.[0]?.method || raw.payment_type,
    payment_state: raw.payment_state,
    payment_status: raw.payment_status,
    // TCR-1967: cột "Số hoá đơn". Mapper là whitelist — quên dòng này thì cột luôn "—".
    e_invoices: raw.e_invoices ?? [],
    // TCR-2625: whitelist field — thiếu dòng này cột "Quà tặng" luôn trống.
    gift_summary: raw.gift_summary ?? null,
    created_at: raw.created_at,
    order_date: raw.order_date,
    shipping: raw.shipping,
    note: raw.note,
    shipping_note: raw.shipping_note,
    // Whitelist này phải RỘNG BẰNG `mapRawOrderDetail` ở phần dòng hàng: menu
    // "..." của màn DANH SÁCH có action "Sao chép đơn hàng" dùng chung
    // `buildCopyOrderItems` với màn chi tiết. Thiếu `product_variant_id` /
    // `serial` / `price_method` / `combo_*` ở đây thì bản sao từ danh sách âm
    // thầm rơi dòng đa biến thể + dòng serial, mất phân bổ giá combo và mất cảnh
    // báo giá vàng cũ — trong khi BE `listOrders()` vẫn project đủ `items`.
    items: rawItems?.map(i => ({
      line_id: i.line_id ? String(i.line_id) : undefined,
      sku: String(i.sku ?? i.product_variant_id ?? ''),
      product_id: i.product_id ? String(i.product_id) : undefined,
      // Giữ id biến thể RIÊNG (không gộp vào `sku`) — xem `mapRawOrderDetail`.
      product_variant_id: i.product_variant_id ? String(i.product_variant_id) : undefined,
      product_name: String(i.product_name ?? i.name ?? i.product_id ?? ''),
      image_url: i.image_url ? String(i.image_url) : undefined,
      warehouse_name: i.warehouse_name ? String(i.warehouse_name) : undefined,
      // TCR-2495: kho xuất per line — popup tồn kho mã ERP đánh dấu kho của đơn.
      warehouse_id: (i as { warehouse_id?: unknown }).warehouse_id
        ? String((i as { warehouse_id?: unknown }).warehouse_id)
        : undefined,
      unit_name: i.unit_name ? String(i.unit_name) : undefined,
      qty: Number(i.qty ?? i.quantity ?? 0),
      price: Number(i.price ?? i.original_price ?? 0),
      discount_type: passThroughString(i.discount_type) as 'percent' | 'amount' | null | undefined,
      discount_percent: passThroughNumber(i.discount_percent),
      discount_amount: passThroughNumber(i.discount_amount),
      vat_rate: passThroughNumber(i.vat_rate),
      vat_amount: passThroughNumber(i.vat_amount),
      line_total: passThroughNumber(i.line_total),
      vat_source: passThroughString(i.vat_source),
      price_method: i.price_method ? String(i.price_method) : 'fixed',
      serial: i.serial ? String(i.serial) : '',
      // TCR virtual-combo — pass through as-is (kể cả null), giống
      // `mapRawOrderDetail`: KHÔNG dùng `||` và KHÔNG default về undefined.
      combo_group_id: passThroughString(i.combo_group_id),
      combo_id: passThroughString(i.combo_id),
      combo_sku: passThroughString(i.combo_sku),
      combo_name: passThroughString(i.combo_name),
      combo_unit_qty: passThroughNumber(i.combo_unit_qty),
      combo_unit_count: passThroughNumber(i.combo_unit_count),
      combo_unit_price: passThroughNumber(i.combo_unit_price),
      combo_applied_unit_price: passThroughNumber(i.combo_applied_unit_price),
      combo_discount_type: passThroughString(i.combo_discount_type) as
        | 'percent'
        | 'amount'
        | null
        | undefined,
      combo_discount_percent: passThroughNumber(i.combo_discount_percent),
      combo_discount_amount: passThroughNumber(i.combo_discount_amount),
      combo_retail_total: passThroughNumber(i.combo_retail_total),
      // TCR-1517 — combo THẬT (khác hẳn khối `combo_*` ở trên, vốn của combo
      // ẢO): snapshot định mức chụp lúc bán, CHỈ để hiển thị. Mapper này
      // whitelist từng field nên không liệt kê ở đây là MẤT HẲN dù BE có trả —
      // đúng lỗi đã làm màn chi tiết đơn không hiện được nút bung thành phần.
      // Pass-through nguyên trạng: `undefined` ở dòng hàng thường chính là tín
      // hiệu "không phải combo thật", đừng default thành `[]`.
      // TCR-2129 — mapper whitelist: pass-through snapshot CK chính sách + lý do.
      policy_discount: i.policy_discount as NonNullable<TenantOrder['items']>[number]['policy_discount'],
      discount_deviation: i.discount_deviation as NonNullable<TenantOrder['items']>[number]['discount_deviation'],
      override_reason: passThroughString(i.override_reason),
      is_combo_product: i.is_combo_product as boolean | undefined,
      bom_components: i.bom_components as
        | Array<{
            sku: string;
            product_name: string;
            unit_name: string;
            qty_per_unit: number;
            /** Ảnh chụp lúc bán — đơn cũ không có, UI tự hiện placeholder. */
            image?: string;
          }>
        | undefined,
    })) ?? [],
    pre_order_flag: raw.pre_order_flag ?? (raw as any).pre_order_flag,
    fulfillment_type: (raw as any).fulfillment_type || raw.fulfillment_type,
    b2b_channel: raw.b2b_channel,
    // TCR-2129 — mapper whitelist: thiếu dòng này thì màn sửa đơn không biết đơn có CK khác chính sách.
    discount_review: raw.discount_review,
    tags: raw.tags,
    skipped: raw.skipped,
    // [Final review fix round 1, 2026-09-07] TCR-1522 — xem doc-comment ở
    // `RawOrder.credit_approval_status`. Default `'not_required'` khi BE
    // không trả field (đơn cũ tạo trước Task 5, hoặc đơn cá nhân/không phải
    // B2B tổ chức) — khớp giá trị mặc định BE gán cho field này lúc tạo đơn
    // bình thường, tránh `undefined` lan ra UI khiến logic `!== 'not_required'`
    // (badge/cảnh báo) phải tự xử lý thêm 1 nhánh rỗng.
    credit_approval_status: raw.credit_approval_status ?? 'not_required',
    // TCR-1823: dùng để hiển thị lý do cụ thể trong popup chặn/xác nhận duyệt đơn.
    credit_approval_reason: raw.credit_approval_reason ?? null,
  };
}

function mapRawOrderDetail(raw: RawOrder & Record<string, unknown>): TenantOrder & { platform?: string | null } {
  const rawItems = raw.items as Array<Record<string, any>> | undefined;
  return {
    _id: raw._id || (raw as any).order_id,
    order_number: raw.order_number,
    channel: raw.channel as TenantOrder['channel'],
    platform: raw.platform,
    user_type: (raw as { user_type?: TenantOrder['user_type'] }).user_type,
    // TCR-1353 review C1 — xem doc-comment ở `RawOrder.user_id`.
    user_id: raw.user_id ? String(raw.user_id) : undefined,
    state: (raw.state || raw.status) as TenantOrder['state'],
    status_group: raw.status_group as TenantOrder['status_group'],
    // TCR-1963 — mapper là whitelist; thiếu dòng này thì badge đếm ngược Nháp không hiện.
    draft_expires_at: (raw as { draft_expires_at?: string | null }).draft_expires_at ?? null,
    delivery_method: raw.delivery_method,
    fulfillment_progress: raw.fulfillment_progress,
    pos_fulfillment: raw.pos_fulfillment,
    return_info: raw.return_info,
    shipping_state: raw.shipping_state,
    total_amount: raw.summary?.total ?? Number(raw.total_amount) ?? 0,
    subtotal: raw.subtotal ?? raw.summary?.final_price ?? raw.summary?.subtotal,
    discount: raw.discount
      ?? ((raw.summary?.direct_discount ?? 0) + (raw.summary?.voucher_discount ?? 0)),
    summary: mapSummary(raw.summary),
    // TCR-1556 — CK tích luỹ B2B. Giữ nguyên `undefined` cho đơn cũ (KHÔNG
    // `?? 0`): payload sửa đơn phân biệt "chưa từng có field" với "đã bỏ chiết
    // khấu" để quyết định có đụng ví CK hay không.
    trade_discount_amount: raw.trade_discount_amount,
    rebate_reserved_amount: raw.rebate_reserved_amount,
    // Field cấp đơn, KHÁC `summary.order_revenue` (xem comment ở `RawOrder`).
    // `getOrderDetail` (BE) KHÔNG set field này (chỉ aggregate `GET /orders`
    // set) — trên màn chi tiết/sửa đơn, field này luôn `undefined`; đọc
    // `summary.order_revenue` (qua `orderToMoneyResult`) mới có số thật.
    order_revenue: raw.order_revenue ?? undefined,
    service_lines: raw.service_lines,
    shipping_fee_estimate: raw.shipping_fee_estimate ?? undefined,
    shipping_fee_actual: raw.shipping_fee_actual ?? undefined,
    created_by_name: raw.created_by_name,
    // TCR-1901: KHÔNG bỏ `store_id` ở đây. Mapper whitelist field nên thiếu một
    // dòng là màn chi tiết đơn mất hẳn chi nhánh — panel QR im lặng rơi về tài
    // khoản mặc định của tenant thay vì tài khoản của chi nhánh.
    store_id: raw.store_id,
    store_name: raw.store_name,
    shipping_city_name: raw.shipping_city_name,
    shipping_ward_name: raw.shipping_ward_name,
    customer: ((raw as any).customer && (raw as any).customer.name) ? {
      name: (raw as any).customer.name,
      phone: (raw as any).customer.phone,
      email: (raw as any).customer.email,
      code: (raw as any).customer.code,
      amis_account_object_id: (raw as any).customer.amis_account_object_id,
    } : {
      name: (raw.buyer_invoice_info as { name?: string } | undefined)?.name
        ?? (raw.shipping as { address?: { name?: string } } | undefined)?.address?.name
        ?? '',
      phone: (raw.buyer_invoice_info as { phone?: string } | undefined)?.phone
        ?? (raw.shipping as { address?: { phone?: string } } | undefined)?.address?.phone,
    },
    payment_method: raw.payment?.method || raw.payment_method || raw.payments?.[0]?.method || raw.payment_type,
    cashier: (raw as any).cashier ? (raw as any).cashier : ((raw as any).cashier_name
      ? { name: (raw as any).cashier_name, staff_id: raw.commission_staff?.find(s => s.role_code === 'NVBH')?.staff_id }
      : raw.commission_staff?.find(s => s.role_code === 'NVBH')
        ? { name: '', staff_id: raw.commission_staff.find(s => s.role_code === 'NVBH')?.staff_id }
        : undefined),
    care_staff: (raw as any).care_staff,
    created_at: raw.created_at,
    note: (raw.notes as { user_note?: string } | undefined)?.user_note || (raw as any).note,
    shipping_note: raw.shipping_note,
    split_from_order_id: raw.split_from_order_id ? String(raw.split_from_order_id) : undefined,
    split_to_order_ids: Array.isArray(raw.split_to_order_ids) ? raw.split_to_order_ids.map(String) : undefined,
    items: rawItems?.map(i => ({
      line_id: i.line_id ? String(i.line_id) : undefined,
      sku: String(i.sku || i.product_variant_id || ''),
      product_id: i.product_id ? String(i.product_id) : undefined,
      // Giữ id biến thể RIÊNG (không gộp vào `sku`): `sku` của dòng đơn là SKU
      // SẢN PHẨM nên tra lookup không ra biến thể nào — màn sửa đơn cần id này
      // để lấy lại ảnh SP / giá lẻ / product_id.
      product_variant_id: i.product_variant_id ? String(i.product_variant_id) : undefined,
      product_name: String(i.product_name || i.name || ''),
      image_url: i.image_url ? String(i.image_url) : undefined,
      // TCR-1962: ĐVT cho tài liệu "Đơn hàng" gửi khách — whitelist ở `mapRawOrder`
      // (danh sách) đã có, thiếu ở đây thì mọi chứng từ dựng từ chi tiết đơn mất ĐVT.
      unit_name: i.unit_name ? String(i.unit_name) : undefined,
      // TCR-1072: ảnh đại diện — BE populate từ Product.medias[0].thumbnail
      // (chỉ getOrderDetail() trả field này, listOrders() dùng image_url ở trên).
      image: i.image ? String(i.image) : undefined,
      qty: Number(i.qty || i.quantity || 0),
      price: Number(i.price || i.unit_price || 0),
      // TCR-1073: đơn giá GỐC trước chiết khấu dòng. Mapper này whitelist từng
      // field nên field nào không liệt kê ở đây là mất hẳn, dù BE có trả —
      // đó chính là lý do màn chi tiết từng hiện giá đã chiết khấu ở cột "Đơn
      // giá". Không dùng `||`: giá gốc 0 là dữ liệu hợp lệ của đơn cũ và phải
      // phân biệt được với "BE không trả field này".
      original_price:
        i.original_price === undefined || i.original_price === null
          ? undefined
          : Number(i.original_price),
      discount_type: passThroughString(i.discount_type) as 'percent' | 'amount' | null | undefined,
      discount_percent: passThroughNumber(i.discount_percent),
      discount_amount: passThroughNumber(i.discount_amount),
      // VAT snapshot theo DÒNG hàng đã lưu. Chỉ dùng hai field canonical của
      // OrderItem schema; giữ 0 và null tường minh, không suy ra từ summary.
      vat_rate: passThroughNumber(i.vat_rate),
      vat_amount: passThroughNumber(i.vat_amount),
      line_total: passThroughNumber(i.line_total),
      vat_source: passThroughString(i.vat_source),
      price_method: i.price_method || 'fixed',
      warehouse_name: i.warehouse_name || '',
      // TCR-2493: MÃ KHO per line (BE `getOrderDetail` populate từ FinishedGoodWarehouse.code).
      // Mapper này chỉ chép field được liệt kê tường minh — thiếu dòng này thì pop-up xem
      // nhanh không bao giờ thấy mã kho dù API đã trả về (Chrome test local phát hiện).
      warehouse_code: (i as { warehouse_code?: unknown }).warehouse_code
        ? String((i as { warehouse_code?: unknown }).warehouse_code)
        : undefined,
      // TCR-629: id kho xuất per line — cần cho màn sửa đơn (đổi kho theo id).
      warehouse_id: (i as { warehouse_id?: unknown }).warehouse_id
        ? String((i as { warehouse_id?: unknown }).warehouse_id)
        : undefined,
      serial: i.serial || '',
      warehouse_staff: i.warehouse_staff,
      seller_staff: i.seller_staff,
      warehouse_staff_id: i.warehouse_staff_id || i.warehouse_staff?.staff_id,
      seller_staff_id: i.seller_staff_id || i.seller_staff?.staff_id,
      // TCR virtual-combo — pass through as-is (kể cả null). KHÔNG dùng `||`
      // (sẽ biến 0/'' hợp lệ thành fallback sai) và KHÔNG default về undefined
      // (mất tín hiệu "BE trả null tường minh" mà groupOrderItemsByCombo dựa vào).
      combo_group_id: i.combo_group_id,
      combo_id: i.combo_id,
      combo_sku: i.combo_sku,
      combo_name: i.combo_name,
      combo_unit_qty: i.combo_unit_qty,
      combo_unit_count: i.combo_unit_count,
      combo_unit_price: i.combo_unit_price,
      combo_applied_unit_price: passThroughNumber(i.combo_applied_unit_price),
      combo_discount_type: passThroughString(i.combo_discount_type) as
        | 'percent'
        | 'amount'
        | null
        | undefined,
      combo_discount_percent: passThroughNumber(i.combo_discount_percent),
      combo_discount_amount: passThroughNumber(i.combo_discount_amount),
      combo_retail_total: i.combo_retail_total,
      // TCR-1517 — combo THẬT: snapshot định mức để hiển thị (xem chú thích
      // cùng tên ở `mapRawOrder`). Whitelist hai bên phải rộng bằng nhau.
      is_combo_product: i.is_combo_product,
      bom_components: i.bom_components,
      // TCR-2129 — pass-through snapshot CK chính sách + lý do (whitelist).
      policy_discount: i.policy_discount,
      discount_deviation: i.discount_deviation,
      override_reason: i.override_reason,
      // TCR-1074: dòng quà tặng BXGY — pass through như combo_*, không dùng
      // `||` (không cần fallback, `is_gift` chỉ true/false/undefined).
      is_gift: i.is_gift,
      gift_promotion_id: i.gift_promotion_id,
      // TCR-1074 (final review, Minor) — mapper này là WHITELIST. BE ĐÃ lưu
      // `gift_source` xuống `Order.items[]` (schema shared, đóng dấu ở cả hai
      // đường voucher/CTKM) và `getOrderDetail` trả nguyên document, nhưng
      // thiếu dòng này thì field bị cắt trước khi tới `Orders/edit.tsx` —
      // buộc FE phải SUY ĐOÁN nguồn quà từ hình dạng `gift_promotion_id`
      // (`inferGiftSource`, regex hex-24), sai với mã voucher trùng dạng.
      gift_source: (i as { gift_source?: unknown }).gift_source as
        | 'voucher'
        | 'promotion'
        | undefined,
      // TCR-1435 (final review, finding C1) — mapper này là WHITELIST, y hệt
      // trap của `gift_source` ở trên (TCR-1074) và `flash_sale_item_ids` bên
      // dưới (TCR-1143): field này ĐÃ có trên `OrderItem` type nhưng thiếu ở
      // đây khiến `item.gift_rebate_accrual_id` LUÔN `undefined` dù BE trả
      // đúng field — 2 badge quà B2B Rebate mới ở `ReadOnlyOrderItemsTable.tsx`
      // (gate `item.is_gift && item.gift_rebate_accrual_id`) không bao giờ
      // render được. Pass through nguyên giá trị (kể cả `null`), không dùng
      // `||`, giống style `gift_source`.
      gift_rebate_accrual_id: (i as { gift_rebate_accrual_id?: unknown }).gift_rebate_accrual_id as
        | string
        | null
        | undefined,
      // TCR-1143 (final review, finding #1): mapper này là WHITELIST — thiếu
      // field này thì `Orders/edit.tsx` không cách nào biết dòng đã lưu là
      // flash sale (BE trả nguyên document ở `getOrderDetail`, field CÓ mặt,
      // chỉ là bị mapper này cắt trước khi tới component). Pass through
      // nguyên mảng, không suy diễn — chỗ dùng (`edit.tsx`) tự lấy phần tử
      // đầu, xem comment ở khai báo field trong `mocks/orders.ts`.
      flash_sale_item_ids: Array.isArray(i.flash_sale_item_ids)
        ? (i.flash_sale_item_ids as unknown[]).map(String)
        : i.flash_sale_item_ids,
    })) ?? [],
    shipping: raw.shipping,
    surcharge_amount: raw.surcharge_amount ?? 0,
    buyer_invoice_info: raw.buyer_invoice_info || (raw as any).buyer_invoice_info,
    store: (raw as any).store,
    invoice_info: raw.invoice_info || (raw as any).invoice_info,
    state_history: raw.state_history || (raw as any).state_history,
    process_logs: raw.process_logs,
    pre_order_flag: raw.pre_order_flag ?? (raw as any).pre_order_flag,
    fulfillment_type: (raw as any).fulfillment_type || raw.fulfillment_type,
    payments: (raw as any).payments || [],
    paid_amount: toNumber((raw as any).paid_amount),
    receipted_amount: toReceiptedAmount((raw as any).receipted_amount),
    // TCR-1077: phần chuyển khoản (CK) khi thanh toán hỗn hợp CK+TM — QR ở chi tiết
    // đơn chỉ quét đúng số CK này, không lấy cả tổng đơn.
    non_cash_amount: raw.payment?.non_cash_amount,
    cash_amount: raw.payment?.cash_amount,
    cash_received: raw.payment?.cash_received,
    change_amount: raw.payment?.change_amount,
    payment_state: raw.payment_state,
    payment_status: raw.payment_status,
    // Mapper này là whitelist — quên hai dòng dưới thì card "Chứng từ kế toán"
    // ở chi tiết đơn không bao giờ có dữ liệu (BE vẫn trả đủ).
    accounting: raw.accounting,
    e_invoice: raw.e_invoice,
    b2b_channel: raw.b2b_channel,
    // TCR-2129 — pass-through (whitelist): cờ review + nhật ký lý do cho màn sửa/chi tiết.
    discount_review: raw.discount_review,
    discount_override_logs: raw.discount_override_logs,
    tags: Array.isArray((raw as { tags?: unknown }).tags)
      ? ((raw as { tags?: unknown }).tags as unknown[]).map(String)
      : undefined,
    skipped: (raw as { skipped?: boolean }).skipped,
    // [Final review fix round 1, 2026-09-07] TCR-1522 — xem doc-comment ở
    // `RawOrder.credit_approval_status` (mapRawOrder ở trên). Cùng default
    // `'not_required'` cho màn CHI TIẾT đơn, khớp mapRawOrder (màn danh sách)
    // để cả 2 màn nhất quán khi BE không trả field.
    credit_approval_status: (raw as { credit_approval_status?: TenantOrder['credit_approval_status'] }).credit_approval_status
      ?? 'not_required',
    // TCR-1823: cùng lý do với mapRawOrder ở trên — mapper whitelist, thiếu dòng này thì
    // popup chặn/xác nhận duyệt đơn ở màn chi tiết luôn hiện message fallback rỗng.
    credit_approval_reason: (raw as { credit_approval_reason?: TenantOrder['credit_approval_reason'] }).credit_approval_reason
      ?? null,
  };
}

/**
 * Legacy POS create order payload — TODO: refactor sang shared schema khi POS module update.
 * Channel union 'pos|online|wholesale' KHÔNG khớp shared OrderChannel 11 enum.
 */
export interface CreateOrderPayload {
  customer_id?: string;
  items: Array<{ product_id: string; qty: number; price: number; discount?: number }>;
  payment_method: 'cash' | 'card' | 'transfer' | 'split';
  channel: 'pos' | 'online' | 'wholesale';
  discount?: number;
  notes?: string;
}

export interface RetryOffPlatformAccountingResponse {
  order_id: string;
  accounting_run_id: string;
  published: number;
}

/**
 * Trả về từ `POST /pos/orders/:id/fulfillment/dispatch/retry` — khác hẳn
 * `RetryOffPlatformAccountingResponse` ở trên: đây là retry của Document
 * Scenario Engine (CTBH/PXK/HĐĐT nháp đơn Ngoài sàn, `process_logs` action
 * `fulfillment.off_platform_scenario.failed`), không phải retry của luồng kế
 * toán event-replay cũ (`pos_fulfillment.accounting_status`). Hai cơ chế độc
 * lập, không dùng chung endpoint.
 */
export interface RetryOffPlatformPackedScenarioResponse {
  status: 'applied' | 'already_applied';
  order_id: string;
  fulfillment_progress: string;
}

/**
 * Body của `POST /orders/:id/receipts` — khớp `CreateOrderReceiptBody` bên
 * be-tenant (`orders-aggregate.service.ts`). `fund_account_id` thay cho
 * `bank_name` (TCR-359 Task 13) — sổ tiền gửi thật đã liên kết AMIS.
 */
export interface OrderReceiptDetail {
  description: string;
  /** Sổ tiền gửi TCR — chỉ có ở dòng chuyển khoản (TK Nợ bắt đầu `112`). */
  fund_account_id?: string;
  bank_account_debt: string;
  bank_account_credit: string;
  amount: number;
}

export interface CreateOrderReceiptBody {
  type: 'cash' | 'transfer' | 'card' | 'split';
  doc_date?: string;
  reason?: string;
  fund_account_id?: string;
  details: OrderReceiptDetail[];
}

export interface UpdateOrderPayload {
  notes?: string;
  store_id?: string;
  sales_person_id?: string;
  care_staff_id?: string;
  customer_id?: string;
  customer_info?: any;
  /**
   * `note` — ghi chú vận chuyển (`order.shipping_note`, top-level, TCR-1221).
   * Các field vận chuyển khác vẫn loose (`Record<string, unknown>`) vì đủ
   * kiểu payload khác nhau tuỳ màn (POS/edit) — không type hoá lại ở đây.
   */
  shipping?: Record<string, unknown> & { note?: string };
  buyer_invoice_info?: any;
  items?: Array<{
    /**
     * BẮT BUỘC trên MỌI entry, kể cả `remove_group:true` — BE DTO khai `sku`
     * non-optional (dùng cho message lỗi/audit ở phía BE), và global
     * validation pipe từ chối request thiếu field này TRƯỚC KHI chạm business
     * logic. Một fix wave trước (Phần C) từng nới field này thành optional để
     * né việc entry `remove_group` không mang `sku` — ĐÃ REVERT (final review
     * fix wave, Critical finding 1): `handleRemoveCombo`/`removeGroupItems`
     * (edit.tsx) giờ luôn gửi kèm `sku` cho cả entry `remove_group`, nên
     * field này không còn lý do để optional.
     */
    sku: string;
    /**
     * order-edit-pos-parity Phần C: `line_id` ĐÃ LƯU của dòng — BE khớp
     * dòng theo field này TRƯỚC (sku, combo_group_id) chỉ dùng khi thiếu.
     * Dòng mới thêm trong phiên sửa hiện tại KHÔNG gửi field này.
     */
    line_id?: string;
    qty?: number;
    price?: number;
    price_method?: string;
    /** TCR-1326: thuế suất VAT sửa tay của dòng; vắng mặt = giữ VAT đã lưu. */
    vat_rate?: number;
    warehouse_name?: string;
    /** TCR-629: kho xuất theo id — BE ưu tiên hơn warehouse_name. */
    warehouse_id?: string;
    serial?: string;
    warehouse_staff_id?: string;
    seller_staff_id?: string;
    /**
     * BẮT BUỘC khi dòng thuộc cụm combo — BE khớp dòng theo cặp
     * (sku, combo_group_id). Thiếu field này với dòng combo, BE trả 400.
     *
     * Khi `clear_combo = true`, field này mang combo_group_id CŨ (đã lưu) chỉ
     * để khớp dòng — KHÔNG có nghĩa dòng còn thuộc cụm. Xem `clear_combo`.
     */
    combo_group_id?: string;
    /**
     * Dòng ĐANG RỜI cụm combo (Task 13 fix round 1, Finding 1) — thu ngân bấm
     * "Tách" trên màn sửa đơn. BE xoá 8 field combo_* trên dòng đã lưu (khớp
     * qua combo_group_id CŨ ở trên) và áp `qty`/`price` DTO như dòng thường.
     */
    clear_combo?: boolean;
    /**
     * order-edit-pos-parity Phần C: xoá TOÀN BỘ cụm combo chứa dòng
     * `line_id` này khỏi đơn — khác `clear_combo` (dòng đó KHÔNG ở lại đơn
     * dưới dạng hàng lẻ). BẮT BUỘC kèm `line_id`.
     */
    remove_group?: boolean;
    /**
     * TCR-1074 (final review, Critical 1) — LỜI KHAI "dòng này là quà của
     * chương trình khuyến mãi `promotion_id`". BE KHÔNG tin thẳng: nó đối
     * chiếu với `free_items` do chính server đánh giá cho giỏ sau khi cập
     * nhật rồi mới đóng dấu `is_gift`/`gift_source` (xem
     * `UpdateOrderItemDto`). Cố ý KHÔNG có `is_gift` ở đây — đó là kết luận
     * của server, không phải dữ liệu client gửi.
     */
    gift_promotion_id?: string;
  }>;
  /**
   * Task 9 (order-edit-pos-parity Phần B) — mã voucher user đã chọn lại trên
   * màn sửa đơn (field ẩn `vouchers` trên form, ghi bởi `VoucherModal` qua
   * `CustomerSidebar`). BE tự re-evaluate/validate lại (không tin FE), rồi
   * trả kết quả qua `voucher_result` trên response — xem `RawOrder.voucher_result`.
   *
   * KHÔNG có `snapshot_id` ở đây: đã xác minh KHÔNG có field `snapshot_id`
   * nào được ghi vào form của màn sửa đơn (chỉ tồn tại ở payload checkout
   * riêng của POS, `POS/types.ts`) — gửi field bịa ra là sai, nên bỏ hẳn cho
   * tới khi có field thật trên form.
   */
  applied_voucher_codes?: string[];
  /**
   * TCR-1353 — số điểm tích luỹ MỚI cho đơn (Task 6, BE). VẮNG MẶT = lần sửa
   * này không đụng tới điểm (BE giữ nguyên bản chụp cũ). CÓ MẶT kể cả `0` =
   * đặt lại thành giá trị này — 0 là lệnh gỡ hết điểm. `edit.tsx` chỉ đưa
   * field này vào object khi người dùng THỰC SỰ đổi số điểm so với số đã
   * lưu trên đơn — KHÔNG bao giờ gửi `null` (BE từ chối 400).
   */
  loyalty_redeem_points?: number;
  /**
   * TCR-1556 — số tiền CK tích luỹ B2B MỚI cho đơn. Cùng quy ước vắng-mặt/
   * bằng-0 với `loyalty_redeem_points` ngay trên: VẮNG MẶT = không đụng ví CK;
   * CÓ MẶT kể cả `0` = đặt lại, với 0 là lệnh bỏ hẳn chiết khấu. `edit.tsx`
   * chỉ đưa field này vào payload khi người dùng thực sự đổi số chiết khấu.
   */
  trade_discount_amount?: number;
}

export const orderService = {
  list: (q: OrderListQuery) => {
    // Resolve group → array enum string cho BE EG-376
    const params: Record<string, unknown> = {
      page: q.page,
      page_size: q.page_size,
      search: q.search,
      date_from: q.date_from,
      date_to: q.date_to,
      created_at_from: q.created_at_from,
      created_at_to: q.created_at_to,
    };
    if (q.order_number) {
      params.order_number = q.order_number;
    }
    if (q.customer) {
      params.customer = q.customer;
    }
    // Chỉ gửi khi có giá trị: gửi undefined/'' làm BE nhận NaN rồi lọc ra 0 kết quả.
    if (q.total_from !== undefined) {
      params.total_from = q.total_from;
    }
    if (q.total_to !== undefined) {
      params.total_to = q.total_to;
    }
    if (q.user_type) {
      params.user_type = q.user_type;
    }
    // TCR-2127 — một lựa chọn Kênh có thể đổi ra tới 3 tham số (channel + user_type + b2b_channel).
    if (q.channel_group) {
      Object.assign(params, resolveChannelFilterParams(q.channel_group));
    }
    // TCR-2257 — `params` là DANH SÁCH TRẮNG: thiếu 2 dòng này thì bộ lọc "Ngoài sàn" của phiếu
    // yêu cầu gửi `channel`/`platform` nhưng chúng không rời khỏi FE, BE nhận đủ đơn POS
    // online/admin thay vì chỉ đơn ngoài sàn. Đặt SAU `channel_group` để lựa chọn tường minh thắng.
    if (q.channel) {
      params.channel = q.channel;
    }
    if (q.platform) {
      params.platform = q.platform;
    }
    if (q.status) {
      params.status = q.status;
    }
    if (q.status_group) {
      params.status_group = q.status_group;
    }
    // TCR-1092: 4 filter bổ sung — chỉ gửi khi có giá trị, giống pattern channel/status ở trên.
    if (q.created_by) {
      params.created_by = q.created_by;
    }
    if (q.fee_payer) {
      params.fee_payer = q.fee_payer;
    }
    if (q.has_customer_note) {
      params.has_customer_note = q.has_customer_note;
    }
    if (q.prepaid) {
      params.prepaid = q.prepaid;
    }
    if (q.payment_state) {
      params.payment_state = q.payment_state;
    }
    // TCR-1967: `params` là DANH SÁCH TRẮNG — thiếu dòng này thì tag "Hoá đơn" vẫn hiện mà bảng
    // không hề được lọc (đã bắt được bằng ảnh chụp, test đơn vị không thấy).
    if (q.invoice_state) {
      params.invoice_state = q.invoice_state;
    }
    // TCR-2625: `params` là danh sách trắng — thiếu thì tag "Quà tặng" hiện mà bảng không lọc.
    if (q.gift_filter) {
      params.gift_filter = q.gift_filter;
    }
    // TCR-2130: đặt SAU `channel_group` để thắng `b2b_channel` mà lựa chọn Kênh gộp đã gán.
    if (q.b2b_channel) {
      params.b2b_channel = q.b2b_channel;
    }
    // TCR-2129: `params` là danh sách trắng — thiếu dòng này thì thẻ lọc hiện mà bảng không lọc.
    if (q.has_discount_deviation === 'true') {
      params.has_discount_deviation = 'true';
    }
    // TCR-1668: lọc theo chi nhánh. `params` ở đây là DANH SÁCH TRẮNG — thiếu một dòng thì
    // param im lặng không rời khỏi FE, giao diện vẫn hiện thẻ điều kiện lọc và bảng vẫn đổi
    // (react-query đổi key nên có refetch), chỉ là kết quả trả về không hề được lọc. Đó đúng
    // là bug QA trả vòng 1 của ticket này.
    if (q.storeId) {
      params.storeId = q.storeId;
    }
    if (q.store_id) {
      params.store_id = q.store_id;
    }
    // TCR-2629: `params` ở đây là DANH SÁCH TRẮNG — thiếu dòng này thì thẻ "Miền giao hàng"
    // hiện trên màn hình nhưng bảng không hề được lọc theo miền.
    if (q.delivery_region) {
      params.delivery_region = q.delivery_region;
    }
    // Sắp xếp phía server. Không gửi thì BE tự dùng `order_date` giảm dần.
    if (q.sort) {
      params.sort = q.sort;
    }
    return api.get('/orders', { params }).then((r) => {
      const raw = unwrap<{ items: RawOrder[]; total: number; summary: OrderSummary }>(r.data);
      return {
        items: raw.items?.map((r) => mapRawOrder(r as RawOrder & Record<string, unknown>)) ?? [],
        total: raw.total ?? 0,
        summary: raw.summary ?? { total_amount: 0, count_by_channel: {} as Record<string, number> },
      };
    });
  },

  getById: (id: string) =>
    api.get(`/orders/${id}`).then((r) => mapRawOrderDetail(unwrap<RawOrder & Record<string, unknown>>(r.data))),

  getDetail: (id: string) => orderService.getById(id),

  retryOffPlatformAccounting: (orderId: string) =>
    api
      .post(`/pos/fulfillment/accounting/orders/${orderId}/retry`)
      .then((r) => unwrap<RetryOffPlatformAccountingResponse>(r.data)),

  /** Retry Document Scenario (CTBH/PXK/HĐĐT nháp) cho đơn Ngoài sàn — resume cùng ScenarioExecution. */
  retryOffPlatformPackedScenario: (orderId: string) =>
    api
      .post(`/pos/orders/${orderId}/fulfillment/dispatch/retry`)
      .then((r) => unwrap<RetryOffPlatformPackedScenarioResponse>(r.data)),

  revealSensitive: (id: string, password: string) =>
    api.post(`/orders/${id}/sensitive`, { password }).then((r) => unwrap<{ citizen_id?: string; invoice_buyer_cccd?: string }>(r.data)),

  listConfirmed: (q: { page?: number; page_size?: number; search?: string }) =>
    api.get('/orders', {
      // BE `GET /orders` đọc param `status` (OrderListQuery.status), gửi `state` là bị bỏ qua.
      params: { ...q, status: 'CONFIRMED,CUSTOMER_CARE_CONFIRMED,WAITING_PICKUP' },
    }).then((r) => unwrap<OrderListResponse>(r.data)),

  cancel: (id: string, reason: string) =>
    api.post(`/orders/${id}/cancel`, { reason }).then((r) => unwrap<TenantOrder>(r.data)),

  /** TCR-1963 — Nháp → Chờ xác nhận. BE trả 400 "Chỉ áp dụng cho đơn nháp" nếu đơn không phải DRAFT. */
  submitDraft: (id: string) =>
    api.post(`/orders/${id}/submit-draft`).then((r) => mapRawOrderDetail(unwrap<RawOrder & Record<string, unknown>>(r.data))),

  /**
   * TCR-1963 — "Xoá hẳn" đơn Nháp (nhả tồn + xoá). `deleted: false` +
   * `cancellation_pending: true` = đơn đã huỷ (nhả tồn) nhưng CHƯA xoá vì hoàn
   * tác khuyến mại đang chạy dở — khác `{ deleted: true }` của brief gốc, đây
   * là shape BE thực tế trả về (xem `task-8-brief.md` ghi chú CHANGE).
   */
  deleteDraft: (id: string) =>
    api.delete(`/orders/${id}`).then((r) => unwrap<{ deleted: boolean; cancellation_pending?: boolean }>(r.data)),

  /** TCR-2129 — kế toán trả lại đơn PENDING để NVKD sửa CK khác chính sách (note bắt buộc). */
  returnDiscountReview: (id: string, note: string) =>
    api.post(`/orders/${id}/discount-review/return`, { note }).then((r) => mapRawOrderDetail(unwrap<RawOrder & Record<string, unknown>>(r.data))),

  confirm: (id: string, body: { confirm_outstanding_debt?: boolean } = {}) =>
    api.post(`/orders/${id}/confirm`, body).then((r) => mapRawOrderDetail(unwrap<RawOrder & Record<string, unknown>>(r.data))),

  ship: (id: string, tracking_number: string, carrier: string) =>
    api.post(`/orders/${id}/ship`, { tracking_number, carrier }).then((r) => mapRawOrderDetail(unwrap<RawOrder & Record<string, unknown>>(r.data))),

  complete: (id: string) =>
    api.post(`/orders/${id}/complete`).then((r) => mapRawOrderDetail(unwrap<RawOrder & Record<string, unknown>>(r.data))),

  skip: (id: string) =>
    api.post(`/orders/${id}/skip`).then((r) => mapRawOrderDetail(unwrap<RawOrder & Record<string, unknown>>(r.data))),

  unskip: (id: string) =>
    api.post(`/orders/${id}/unskip`).then((r) => mapRawOrderDetail(unwrap<RawOrder & Record<string, unknown>>(r.data))),

  // Legacy POS endpoints — keep cho POS module hiện tại, refactor khi POS update sang shared schema
  create: (body: CreateOrderPayload) =>
    api.post<TenantOrder>('/orders', body).then((r) => r.data),

  createPosOrder: (body: CreateOrderPayload) =>
    api.post<TenantOrder>('/orders/pos', body).then((r) => r.data),

  history: () => api.get<TenantOrder[]>('/orders/history').then((r) => r.data),

  exportList: (filter: OrderListQuery) => {
    const params: Record<string, unknown> = {
      gift_only: filter.gift_only,
      has_sales_document: filter.has_sales_document,
      has_e_invoice: filter.has_e_invoice,
      sort: filter.sort,
      search: filter.search,
      date_from: filter.date_from,
      date_to: filter.date_to,
      created_at_from: filter.created_at_from,
      created_at_to: filter.created_at_to,
    };
    if (filter.channel_group) {
      Object.assign(params, resolveChannelFilterParams(filter.channel_group));
    }
    if (filter.status) {
      params.status = filter.status;
    }
    if (filter.status_group) {
      params.status_group = filter.status_group;
    }
    // TCR-1092: 4 filter bổ sung phải đi CẢ đường export — nếu không, người dùng
    // lọc còn 12 đơn rồi bấm "Xuất Excel" lại nhận về toàn bộ đơn khớp các filter
    // cũ. Cùng pattern "chỉ gửi khi có giá trị" như `list` ở trên.
    if (filter.created_by) {
      params.created_by = filter.created_by;
    }
    if (filter.fee_payer) {
      params.fee_payer = filter.fee_payer;
    }
    if (filter.has_customer_note) {
      params.has_customer_note = filter.has_customer_note;
    }
    if (filter.prepaid) {
      params.prepaid = filter.prepaid;
    }
    if (filter.payment_state) {
      params.payment_state = filter.payment_state;
    }
    // TCR-1967: file Excel phải khớp bảng đang lọc theo trạng thái hoá đơn.
    if (filter.invoice_state) {
      params.invoice_state = filter.invoice_state;
    }
    // TCR-2625: file Excel phải khớp bảng đang lọc theo quà tặng.
    if (filter.gift_filter) {
      params.gift_filter = filter.gift_filter;
    }
    // TCR-2130: file Excel phải khớp bảng đang lọc theo kênh B2B.
    if (filter.b2b_channel) {
      params.b2b_channel = filter.b2b_channel;
    }
    // TCR-2129: file Excel phải khớp bảng đang lọc CK khác chính sách.
    if (filter.has_discount_deviation === 'true') {
      params.has_discount_deviation = 'true';
    }
    // TCR-1668: chi nhánh phải đi CẢ đường export, cùng lý do như 4 filter TCR-1092 ở trên —
    // lọc còn 10 đơn của một chi nhánh rồi bấm "Xuất Excel" mà nhận về đơn của mọi chi nhánh.
    if (filter.storeId) {
      params.storeId = filter.storeId;
    }
    if (filter.store_id) {
      params.store_id = filter.store_id;
    }
    // TCR-2629: miền giao hàng phải đi CẢ đường export — lọc còn 10 đơn của Miền Nam rồi
    // bấm "Xuất Excel" mà nhận về đơn của cả 3 miền thì file xuất ra rộng hơn bảng đang xem.
    if (filter.delivery_region) {
      params.delivery_region = filter.delivery_region;
    }
    return api.get('/orders/export', { params }).then((r) => {
      const raw = unwrap<{ items: RawOrder[]; total: number }>(r.data);
      return {
        items: raw.items?.map((item) => mapRawOrder(item as RawOrder & Record<string, unknown>)) ?? [],
        total: raw.total ?? 0,
      };
    });
  },

  update: (id: string, payload: UpdateOrderPayload) =>
    api.patch(`/orders/${id}`, payload).then((r) => {
      const raw = unwrap<RawOrder & Record<string, unknown>>(r.data);
      // `mapRawOrderDetail` là whitelist mapper cho `TenantOrder` — `voucher_result`
      // (Task 9) không phải field hiển thị của đơn nên KHÔNG liệt kê trong đó.
      // Gắn riêng ở đây để `handleSave` (edit.tsx) đọc lại được kết quả BE
      // re-evaluate voucher lúc lưu.
      return { ...mapRawOrderDetail(raw), voucher_result: raw.voucher_result };
    }),

  updateNote: (id: string, note: string) =>
    orderService.update(id, { notes: note }),

  updateBranch: (id: string, branch_id: string) =>
    orderService.update(id, { store_id: branch_id }),

  updateStaff: (id: string, staff_id: string) =>
    orderService.update(id, { sales_person_id: staff_id }),

  updateWarehouseStaff: (id: string, item_sku: string, staff_id: string, _staff_name: string) =>
    orderService.update(id, { items: [{ sku: item_sku, warehouse_staff_id: staff_id }] }),

  updateCustomer: (id: string, customer_id: string) =>
    orderService.update(id, { customer_id }),

  updateShipping: (id: string, shipping: { name: string; phone: string; address: string; province?: string; citizen_id?: string; delivery_days?: number; expected_delivery_date?: string; delivery_method?: string }) =>
    orderService.update(id, { shipping }),

  updateInvoice: (id: string, invoice: { name?: string; tax_id?: string; address?: string; email?: string; buyer_name?: string; buyer_cccd?: string }) =>
    orderService.update(id, { buyer_invoice_info: invoice }),

  updateItems: (id: string, items: Array<{ sku: string; qty: number; price: number; price_method?: string; warehouse_name?: string; serial?: string }>) =>
    orderService.update(id, { items }),

  /**
   * TCR-1125 vòng 2 — `lines` mang theo SỐ LƯỢNG tách của từng dòng. BE vẫn
   * nhận dạng cũ `line_ids` (tách nguyên dòng) để deploy BE-trước-FE không gãy,
   * nhưng FE chỉ còn gửi `lines`.
   */
  split: (id: string, body: { lines: Array<{ line_id: string; quantity: number }> }) =>
    api.post(`/orders/${id}/split`, body).then((r) =>
      unwrap<{ original_order: RawOrder & Record<string, unknown>; new_order: RawOrder & Record<string, unknown> }>(r.data)),

  createReceipt: (id: string, receipt: CreateOrderReceiptBody) =>
    api.post(`/orders/${id}/receipts`, receipt).then((r) => mapRawOrderDetail(unwrap<RawOrder & Record<string, unknown>>(r.data))),

  /**
   * TCR-1207 — xoá (HUỶ) một phiếu thu của đơn. `voucherId` chính là `doc_id` của chứng từ
   * trong `accounting.documents[]` — cùng khoá mà `buildOrderReceipts` dùng làm `id`/`docId`.
   * BE trả về nguyên đơn sau khi huỷ (đã trừ lại tiền đã thu), nên FE thay thẳng cache detail.
   */
  deleteReceipt: (id: string, voucherId: string, reason?: string) =>
    api
      .delete(`/orders/${id}/receipts/${voucherId}`, reason ? { data: { reason } } : undefined)
      .then((r) => mapRawOrderDetail(unwrap<RawOrder & Record<string, unknown>>(r.data))),


  getAuditHistory: (id: string, page: number = 1, pageSize: number = 20) =>
    api.get(`/orders/${id}/audit-history`, { params: { page, pageSize } }).then((r) => unwrap<any>(r.data)),
};

export const splitOrderResult = (raw: { original_order: RawOrder & Record<string, unknown>; new_order: RawOrder & Record<string, unknown> }) => ({
  original_order: mapRawOrderDetail(raw.original_order),
  new_order: mapRawOrderDetail(raw.new_order),
});
